import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@shared-kernel/primary/shared/shadcn/ui/avatar";
import type { DetailPageAvatarProps } from "../types";

interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  backLink?: string;
  backLabel?: string;
  avatar?: DetailPageAvatarProps;
}

export const PageHeader = ({ title, subtitle, action, backLink, backLabel, avatar }: PageHeaderProps) => {
  if (backLink) {
    const displaySubtitle = subtitle ?? backLabel;
    return (
      <div className="flex items-center gap-3">
        <Link
          to={backLink}
          className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          aria-label={backLabel ?? "Retour"}
        >
          <ChevronLeft className="size-5" />
        </Link>
        {avatar && (
          <Avatar className="size-12">
            {avatar.src && <AvatarImage src={avatar.src} alt={title} />}
            <AvatarFallback className="bg-muted text-muted-foreground">{avatar.fallback}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex flex-1 items-start justify-between gap-4">
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold leading-tight">{title}</h1>
            {displaySubtitle && (
              <span className="text-sm text-muted-foreground">
                {typeof displaySubtitle === "string" ? displaySubtitle : displaySubtitle}
              </span>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold">{title}</h1>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {subtitle && (typeof subtitle === "string" ? <p className="text-muted-foreground">{subtitle}</p> : subtitle)}
    </div>
  );
};
